import React, { useMemo } from "react";
import {
  MATRIX_AXES,
  MATRIX_POSITION,
  MATRIX_DESCRIPTION,
} from "@/data/fokustypErkennungsmerkmale";

// Sprint 11.6 Block 2 — Task 3: 2x2-Matrix-Visualisierung
// Achsen: Wollen (oben) / Tun-Können (unten) × Emotional (links) / Rational (rechts)
// NICHT „Kopf/Bauch" (siehe User-Spec, 30.06.2026)
// Positionen: Katze (o.l.) | Eule (o.r.) | Hund (u.l.) | Biene (u.r.)
//
// Props:
//  - primaryFokustyp: User's eigener FT (Stern-Marker)
//  - secondaryFokustyp: 2. FT-Tendenz (Ring-Marker, optional)
//  - accent: Akzentfarbe (Default #4563BB)

const FT_LABEL = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

const QUADRANT_STYLE = {
  // Reihenfolge: top-left, top-right, bottom-left, bottom-right
  // entspricht: Katze, Eule, Hund, Biene
  topLeft: { ft: "katze", bg: "rgba(251, 191, 36, 0.08)", border: "rgba(251, 191, 36, 0.35)" },
  topRight: { ft: "eule", bg: "rgba(59, 130, 246, 0.08)", border: "rgba(59, 130, 246, 0.35)" },
  bottomLeft: { ft: "hund", bg: "rgba(249, 115, 22, 0.08)", border: "rgba(249, 115, 22, 0.35)" },
  bottomRight: { ft: "biene", bg: "rgba(16, 185, 129, 0.08)", border: "rgba(16, 185, 129, 0.35)" },
};

function QuadrantCell({ pos, ft, isPrimary, isSecondary }) {
  const cfg = QUADRANT_STYLE[pos];
  const label = FT_LABEL[ft];
  const isUserHere = isPrimary || isSecondary;
  return (
    <div
      data-testid={`matrix-quadrant-${ft}`}
      className="relative flex flex-col items-center justify-center text-center px-2 py-3"
      style={{
        background: cfg.bg,
        border: `1px dashed ${cfg.border}`,
      }}
    >
      <p
        className={`text-xs sm:text-sm font-bold ${
          isPrimary ? "text-slate-900 dark:text-slate-100" : "text-slate-500 dark:text-slate-400"
        }`}
      >
        {label}
      </p>
      {isUserHere && (
        <span
          aria-hidden="true"
          data-testid={`matrix-marker-${isPrimary ? "primary" : "secondary"}`}
          className={`mt-1 inline-flex items-center justify-center rounded-full text-[10px] font-bold ${
            isPrimary
              ? "w-6 h-6 bg-[#4563BB] text-white shadow-md"
              : "w-5 h-5 border-2 border-[#4563BB] text-[#4563BB] bg-white"
          }`}
        >
          {isPrimary ? "★" : "○"}
        </span>
      )}
    </div>
  );
}

export default function FokustypMatrix2x2({ primaryFokustyp, secondaryFokustyp, accent = "#4563BB" }) {
  const primary = (primaryFokustyp || "").toLowerCase();
  const secondary = (secondaryFokustyp || "").toLowerCase();

  const desc = useMemo(() => MATRIX_DESCRIPTION[primary] || null, [primary]);

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 dark:border-slate-700 dark:bg-slate-800/60"
      data-testid="steckbrief-matrix-2x2"
    >
      <p className="text-xs font-bold uppercase tracking-widest" style={{ color: accent }}>
        Positionierung
      </p>
      <h3 className="mt-1 text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">
        Die 4 Fokustypen im Überblick
      </h3>
      <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
        Zwei Achsen zeigen, wo dein Fokustyp und die anderen drei in Konflikten stehen.
      </p>

      <div className="mt-6">
        {/* Wrapper für Achsen-Beschriftung */}
        <div className="grid" style={{ gridTemplateColumns: "auto 1fr", gridTemplateRows: "1fr auto", gap: "8px" }}>
          {/* Y-Label oben */}
          <div className="flex items-center justify-end pr-2">
            <div className="flex flex-col text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
              <span data-testid="matrix-axis-y-top">{MATRIX_AXES.y.top}</span>
              <span className="mt-auto pt-12" data-testid="matrix-axis-y-bottom">{MATRIX_AXES.y.bottom}</span>
            </div>
          </div>

          {/* Matrix-Body 2x2 */}
          <div
            className="grid grid-cols-2 grid-rows-2 rounded-xl overflow-hidden border-2 border-slate-200 dark:border-slate-700"
            style={{ minHeight: 240 }}
            data-testid="matrix-grid"
          >
            <QuadrantCell pos="topLeft" ft="katze" isPrimary={primary === "katze"} isSecondary={secondary === "katze"} />
            <QuadrantCell pos="topRight" ft="eule" isPrimary={primary === "eule"} isSecondary={secondary === "eule"} />
            <QuadrantCell pos="bottomLeft" ft="hund" isPrimary={primary === "hund"} isSecondary={secondary === "hund"} />
            <QuadrantCell pos="bottomRight" ft="biene" isPrimary={primary === "biene"} isSecondary={secondary === "biene"} />
          </div>

          {/* Empty cell unter Y-Label */}
          <div />

          {/* X-Achse darunter */}
          <div className="flex justify-between text-[11px] text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider pt-1">
            <span data-testid="matrix-axis-x-left">{MATRIX_AXES.x.left}</span>
            <span data-testid="matrix-axis-x-right">{MATRIX_AXES.x.right}</span>
          </div>
        </div>
      </div>

      {/* Legende */}
      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-slate-600 dark:text-slate-400">
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#4563BB] text-white text-[9px] font-bold">★</span>
          Dein primärer Fokustyp
        </span>
        {secondary && secondary !== primary && (
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-flex items-center justify-center w-5 h-5 rounded-full border-2 border-[#4563BB] bg-white dark:bg-slate-800 text-[9px] font-bold text-[#4563BB]">○</span>
            2. Tendenz
          </span>
        )}
      </div>

      {desc && (
        <p
          className="mt-4 rounded-lg bg-slate-50 dark:bg-slate-900/40 border-l-4 px-4 py-3 text-sm text-slate-800 dark:text-slate-200 leading-relaxed"
          style={{ borderColor: accent }}
          data-testid="matrix-description"
        >
          <strong className="font-semibold">{FT_LABEL[primary]}:</strong> {desc}
        </p>
      )}
    </section>
  );
}
